import { yupResolver } from "@hookform/resolvers/yup";
import * as NewsletterStepTracker from "@omni/services/tracking/trackers/tools/newsletter/newsletterStep.tracker";
import { Button } from "@omni/ui/components/Button/button.component";
import queryString from "query-string";
import { useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { FormTouchedCallback } from "@/components/Form/RHF/Callbacks/formFieldCallbacks.component";
import { StoreField } from "@/components/Form/RHF/FormFields/StoreField/storeField.container";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { APIStatus } from "@/redux/modules/api/data/customer/customer.module";
import { SubscriptionRequest } from "@/redux/modules/api/data/customer/customer.types";
import { getShopNames } from "@/redux/modules/api/data/stores/shopNames/shopNames.selector";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";

import { useOmniForm } from "../Form/RHF/Utils/useOmniForm";
import {
    SubscriptionFormValidationSchema,
    SubscriptionFormValues
} from "./newsletterSubscription.schema";

const FORM_ID = "newsletterSubscriptionForm";

type SubscriptionFormProps = {
    isLoading: boolean;
    onSubmit: (info: SubscriptionRequest) => Promise<any>;
};

const SubscriptionForm = ({ onSubmit, isLoading }: SubscriptionFormProps) => {
    const { t } = useGranite();
    const shopNames = useSelector(getShopNames, shallowEqual);
    const isClient = useIsClientSide();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            storeId: ""
        },
        resolver: yupResolver(SubscriptionFormValidationSchema)
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
    useEffect(() => {
        if (!isClient) return;
        const formEmail = methods.getValues("email");
        const emailFromQuery = queryString.parse(location.search).email as
            | string
            | null;

        if (emailFromQuery && formEmail === "") {
            methods.setValue("email", emailFromQuery);
        }
    }, [isClient]);

    const onFormTouched = () => {
        NewsletterStepTracker.trackStart();
    };

    const onFormSubmit = async ({
        email,
        firstName,
        lastName,
        storeId
    }: SubscriptionFormValues) => {
        clearMessageById("newsletter-subscription-success");
        clearMessageById("newsletter-subscription-failure");
        const action = await onSubmit({
            email: email,
            firstName: firstName,
            lastName: lastName,
            storeId: storeId
        });

        if (action.type === APIStatus.SUCCESS) {
            NewsletterStepTracker.trackSubmit({
                storeId,
                email
            });
            broadcastMessage({
                id: "newsletter-subscription-success",
                text: t("newsletter.subscription.success"),
                status: "success",
                dismissible: true,
                activeScroll: true
            });
            methods.reset();
        } else {
            broadcastMessage({
                id: "newsletter-subscription-failure",
                text: t("newsletter.subscription.failure"),
                status: "danger",
                dismissible: true,
                activeScroll: true
            });
        }
    };

    return (
        <FormInfoProvider
            formId={FORM_ID}
            withNeverbounce={true}
        >
            {({ initialized }) => (
                <OmniFormContextProvider {...methods}>
                    <form
                        id={FORM_ID}
                        onSubmit={methods.handleSubmit(onFormSubmit)}
                    >
                        <FormTouchedCallback callback={onFormTouched} />
                        <Row>
                            <Col
                                xs={12}
                                md={8}
                            >
                                <TextField<SubscriptionFormValues>
                                    name="email"
                                    label={t("general.form.email.label")}
                                    required
                                    autoComplete={"email"}
                                    placeholder={t(
                                        "general.form.email.placeholder"
                                    )}
                                    disabled={isLoading || !initialized}
                                />
                            </Col>
                            <Col
                                xs={12}
                                md={8}
                            >
                                <TextField<SubscriptionFormValues>
                                    name="firstName"
                                    required={false}
                                    label={t("general.form.firstName.label")}
                                    autoComplete={"given-name"}
                                    placeholder={t(
                                        "general.form.firstName.placeholder"
                                    )}
                                    disabled={isLoading || !initialized}
                                />
                            </Col>
                            <Col
                                xs={12}
                                md={8}
                            >
                                <TextField<SubscriptionFormValues>
                                    name="lastName"
                                    required={false}
                                    label={t("general.form.lastName.label")}
                                    autoComplete={"family-name"}
                                    placeholder={t(
                                        "general.form.lastName.placeholder"
                                    )}
                                    disabled={isLoading || !initialized}
                                />
                            </Col>
                            <Col
                                xs={12}
                                md={8}
                            >
                                <StoreField<SubscriptionFormValues>
                                    name="storeId"
                                    label={t("general.form.store.label")}
                                    placeholder={t(
                                        "general.form.store.placeholder"
                                    )}
                                    required
                                    disabled={isLoading || !initialized}
                                    storeList={shopNames}
                                />
                            </Col>
                        </Row>

                        <Button
                            test="newsletterSubscription-button-submit"
                            type={"submit"}
                            color={"primary"}
                            disabled={isLoading || !initialized}
                        >
                            {t("newsletter.subscription.submit")}
                        </Button>
                    </form>
                </OmniFormContextProvider>
            )}
        </FormInfoProvider>
    );
};

export default SubscriptionForm;
